import { UTILS } from '@aksia/infrastructure';

export const DOMAIN_API_REQUESTS = {
  TAXONOMY: 'Taxonomy Info',
  TAXONOMY_MAIN: 'Taxonomy Main Hierarchy',
  TAXONOMY_CROSS_STRATEGY_TAGS: 'Taxonomy Cross Strategy Tags',
  TAXONOMY_CROSS_STRATEGY_RULES: 'Taxonomy Cross Strategy Rules',
  TAXONOMY_ASSET_CLASS_SUBSTRATEGY_TAGS:
    'Taxonomy Asset Class Substrategy Tags',
  TAXONOMY_PRIMARY_REGIONS: 'Taxonomy Primary Regions',
  COMPANY: 'Company Info',
  LEGAL_REGION: 'Legal Region Info',
  INVESTMENT_PROGRAM: 'Investment Program Info',
  INVESTMENT_VEHICLE: 'Investment Vehicle Info',
  SHARE_CLASSES: 'Share Class Info',
  PERIODIC_STREAMS: 'Periodic Streams Info',
  PERIODIC_STREAMS_AUM: 'Periodic Streams AUM Info',
  PERIODIC_STREAMS_PRIVATE_RETURNS: 'Periodic Streams Private Returns Info',
  DATACIZE_STREAMS: 'Datacize Streams Info',
  METADATA: 'Notes Info',
  EMAIL_EXTRACTION: 'Email Info',
  EMAIL_DATA_POINT: 'Data Point',
  RATINGS: 'Ratings Info',
  toKeyValue: UTILS.KEYVALUE.toKeyValue,
} as const;

export type DomainApiRequestType =
  (typeof DOMAIN_API_REQUESTS)[keyof typeof DOMAIN_API_REQUESTS];

export const DOMAINS = {
  COMPANY: {
    URI: {
      GET: 'basicdata/managementcompany/',
      SET: 'basicdata/managementcompany',
    },
  },
  LEGAL_REGION: {
    URI: {
      GET: 'clientpermission/{managementCompanyId}/managerallowedcountries',
      SET: 'clientpermission/integration/managerallowedcountries',
      AUDIT: 'clientpermission/audit/managerallowedcountries/',
    },
  },
  INVESTMENT_PROGRAM: {
    URI: {
      GET: 'basicdata/investmentprogram/',
      SET: 'basicdata/investmentprogram',
    },
  },
  TAXONOMY_MAIN: {
    URI: {
      GET: 'taxonomy/sector/all',
    },
  },
  TAXONOMY_CROSS_STRATEGY_TAGS: {
    URI: {
      GET: 'taxonomy/crossstrategytag/all',
    },
  },
  TAXONOMY_CROSS_STRATEGY_RULES: {
    URI: {
      GET: 'basicdata/fund/crossstrategytagrule/all',
    },
  },
  TAXONOMY_ASSET_CLASS_SUBSTRATEGY_TAGS: {
    URI: {
      GET: 'taxonomy/assetclasssubstrategy/all',
    },
  },
  TAXONOMY_PRIMARY_REGIONS: {
    URI: {
      GET: 'taxonomy/primaryRegion/all',
    },
  },
  INVESTMENT_VEHICLE: {
    URI: {
      GET: 'basicdata/fund/details/',
      SET: 'basicdata/fund',
    },
  },
  SHARECLASSES: {
    URI: {
      GET: 'shareclass/fund/',
      SET: 'shareclass/{liquidityType}',
    },
  },
  PERIODIC_STREAMS: {
    GROSSIRR: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    NETIRR: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    GROSSMOIC: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    NETMOIC: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    NETDPI: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    NETRVPI: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    INVESTED_CAPITAL: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    NETINCOMERETURN: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    GROSSINCOMERETURN: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    NETAPPRECIATIONRETURN: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    GROSSAPPRECIATIONRETURN: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    TOTALNETRETURN: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    TOTALGROSSRETURN: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/returnprofile/{streamType}/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    SCALEUPRATIO: {
      HTTP: 'PUT',
      URI: {
        SET: 'portco/funddeals/scaleupratio/{entityId}/stream/edit?periodicity={periodicity}',
      },
    },
    PUBLIC_RETURNS: {
      HTTP: 'PUT',
      URI: {
        SET: 'periodicdata/publicreturns/actual/{entityId}/stream/edit',
      },
    },
    COMPANY_AUM: {
      HTTP: 'POST',
      URI: {
        SET: 'aum/bulk',
      },
    },
    PROGRAM_AUM: {
      HTTP: 'POST',
      URI: {
        SET: 'aum/bulk',
      },
    },
    FUND_AUM: {
      HTTP: 'POST',
      URI: {
        SET: 'aum/bulk',
      },
    },
  },
  DATACIZE_STREAMS: {
    INVESTMENT_VEHICLE: {
      URI: {
        GET: 'periodicdata/fundtimeseriesdata/alldatagroups/{entityId}/stream/allbygroup',
        SET: 'periodicdata/fundtimeseriesdata/alldatagroups/{entityId}/stream/edit',
      },
    },
    COMPANY: {
      URI: {
        GET: 'periodicdata/managementcompanytimeseriesdata/alldatagroups/{entityId}/stream/allbygroup',
        SET: 'periodicdata/managementcompanytimeseriesdata/alldatagroups/{entityId}/stream/edit',
      },
    },
  },
  METADATA: {
    URI: {
      GET: 'metadata/get/bulk',
      SET: 'metadata/manage/bulk',
    },
  },
  EMAIL: {
    URI: {
      GET_ALL: 'extractions',
      GET: 'extractions/{id}',
      NEXT_AVAILABLE: 'extractions/next-available',
      SEARCH:
        'extractions/search-entities?searchText={searchText}&dataPointType={dataPointType}',
      UPDATE: 'extractions/update',
      RERUN: 'extractions/{id}/rerun',
      ASSIGN: 'extractions/assign',
      DOWNLOAD_ALL: 'extractions/{id}/attachments/download/full',
      DOWNLOAD_ATTACHMENTS: 'extractions/{id}/attachments/download',
      UPLOAD: 'extractions/{id}/file-uploads',
    },
  },
  MANUAL_UPLOAD: {
    URI: {
      CREATE: 'manual-uploads',
    },
  },
  EXTRACTION_AUDIT: {
    URI: {
      GET_EVENTS:
        'extraction-audit/events?type={type}&id={id}&include={include}',
    },
  },
  EMAIL_DATA_POINT: {
    URI: {
      UPDATE: 'extractions/{id}/results/update',
      ASSIGN: 'extractions/{id}/results/bulk-assign-entity',
      USER_ACTION: 'extractions/{id}/results/{resultId}/user-action',
      ADD: 'extractions/{id}/results',
      SEARCH_DC_FUNDS:
        'extractions/search-dc-funds-by-name?searchText={searchText}&documentEffectiveDate={documentEffectiveDate}&documentType={documentType}',
      SEARCH_DC_CLIENTS: 'extractions/search-dc-clients-by-fundids',
    },
  },
  EXTRACTION_TAG_ASSIGNMENT: {
    URI: {
      GET_ALL: 'ExtractionTagAssignments',
      CREATE: 'ExtractionTagAssignments',
      UPDATE: 'ExtractionTagAssignments/{id}',
      DELETE: 'ExtractionTagAssignments/{id}',
    },
  },
  USERS: {
    URI: {
      GET: 'user/by-client/{clientId}?roles={role}',
    },
  },
  RATINGS: {
    URI: {
      GET_RULES: 'ratings/rules',
      GET_ACTIVE: 'ratings/{entityType}/{entityId}/active',
      GET_HISTORY: 'ratings/{entityType}/{entityId}/history',
      EXPORT_HISTORY: 'ratings/{entityType}/{entityId}/exporthistory',
      SET_IDD: 'ratings/update/IDD',
      SET_ODD: 'ratings/update/ODD',
    },
  },
  DATASINK: {
    URI: {
      GET_EMAILS: 'datasink/emails',
      GET_EMAIL_DETAILS: 'datasink/emails/{id}/details',
      MAKE_CANDIDATE: 'extractions/emails/{id}/candidate',
    },
  },
} as const;

export type DomainType = (typeof DOMAINS)[keyof typeof DOMAINS];
